
import { Vue, Component, Watch } from "vue-property-decorator";
import StockSkuModule from "@/store/modules/barcodeSystem/stockSku";
import BarcodeManufacturerModule from "@/store/modules/barcodeSystem/barcodeManufacturer";
import BarcodeCompanyModule from "@/store/modules/barcodeSystem/barcodeCompany";
import { getModule } from "vuex-module-decorators";
import Select from "@/components/form/Select.vue";
import { StockSkuResource } from "../../models/api/barcodeSystem/stockSkus/get";
import { ManufacturerResource } from "../../models/api/barcodeSystem/barcodeManufacturers/get";
import { EditStockSkuModel } from "@/models/ui/editStockSku";
import { formatDateWithTime } from "@/utils";
import notifications from "../../lib/notifications";

const stockSkuModuleState = getModule(StockSkuModule);
const barcodeManufacturerModuleState = getModule(BarcodeManufacturerModule);
const barcodeCompanyModuleState = getModule(BarcodeCompanyModule);

@Component({
  components: {
    Select
  }
})
export default class StockSkus extends Vue {
  companyIdFilter: number | null = null;
  manufacturerCodeFilter: string = null;
  loading: boolean = false;
  stockSkus: StockSkuResource[] = null;
  activeOnly: boolean = true;
  editStockSkuModel: EditStockSkuModel = null;
  yesNoOptions = [
    { text: 'Yes', value: true },
    { text: 'No', value: false }
  ];

  importModalVisible: boolean = false;
  importCompanyId: number | null = null;
  importFile: any = null;
  importing: boolean = false;

  companyManufacturers: ManufacturerResource[] = [];
  editCompanyManufacturers: ManufacturerResource[] = [];
  importCompanyManufacturers: ManufacturerResource[] = [];

  async created() {
  }

  async fetchManufacturersForCompany(companyId: number): Promise<ManufacturerResource[]> {
    if (!companyId) return [];
    try {
      return await barcodeManufacturerModuleState.getManufacturersForCompanies([companyId]);
    } catch (ex) {
      notifications.error("Load Manufacturers", "Failed to load manufacturers for company", ex);
      return [];
    }
  }

  @Watch('companyIdFilter')
  async onCompanyIdFilterChanged(companyId: number) {
    this.manufacturerCodeFilter = null;
    this.companyManufacturers = await this.fetchManufacturersForCompany(companyId);
  }

  @Watch('editStockSkuModel.companyId')
  async onEditCompanyIdChanged(companyId: number) {
    this.editCompanyManufacturers = await this.fetchManufacturersForCompany(companyId);
  }

  @Watch('importCompanyId')
  async onImportCompanyIdChanged(companyId: number) {
    this.importCompanyManufacturers = await this.fetchManufacturersForCompany(companyId);
  }

  get manufacturerFilterOptions() {
    return this.companyManufacturers
      .filter(x => x.allowManualStockOrdersCreation)
      .map(x => ({ label: x.agentManufacturerCode ?? x.manufacturerName, value: x.code }));
  }

  get companyIds() {
    if (this.companyIdFilter)
      return [this.companyIdFilter];
    return null;
  }

  get filteredStockSkus(): StockSkuResource[] {
    if (!this.stockSkus) return [];
    return this.activeOnly ? this.stockSkus.filter(x => x.isActive) : this.stockSkus;
  }

  get canFilter() {
    return !!this.companyIdFilter;
  }

  async filter() {
    if (!this.canFilter) return;
    await this.loadStockSkus();
  }

  async loadStockSkus() {
    this.loading = true;
    try {
      await stockSkuModuleState.loadStockSkus({
        companyId: this.companyIdFilter,
        manufacturerCode: this.manufacturerCodeFilter
      });
      this.stockSkus = stockSkuModuleState.stockSkus;
    } catch (ex) {
      notifications.error("Search Failed", "Failed to load stock SKUs", ex);
    }
    this.loading = false;
  }

  format(dateToFormat) {
    return formatDateWithTime(dateToFormat);
  }

  companyName(companyId: number) {
    const company = (barcodeCompanyModuleState.companies || []).find(x => x.id === companyId);
    return company ? company.companyName : "";
  }

  manufacturerName(manufacturerCode: string) {
    const manufacturer = (barcodeManufacturerModuleState.manufacturers || []).find(x => x.code === manufacturerCode);
    return manufacturer ? (manufacturer.agentManufacturerCode ?? manufacturer.manufacturerName) : "";
  }

  get manufacturerForCompany(): ManufacturerResource {
    const companyId = this.editStockSkuModel ? this.editStockSkuModel.companyId : null;
    if (!companyId) return null;
    return this.editCompanyManufacturers.find(x => x.allowManualStockOrdersCreation) || null;
  }

  get manufacturerDisplayName() {
    const manufacturer = this.manufacturerForCompany;
    return manufacturer ? (manufacturer.agentManufacturerCode ?? manufacturer.manufacturerName) : "";
  }

  companyIdState() {
    return this.editStockSkuModel.companyId ? null : false;
  }

  skuState() {
    return this.editStockSkuModel.sku ? null : false;
  }

  commodityState() {
    return this.editStockSkuModel.commodity ? null : false;
  }

  itemDescriptionState() {
    return this.editStockSkuModel.itemDescription ? null : false;
  }

  validateForm() {
    if (this.companyIdState() === false) return false;
    if (this.skuState() === false) return false;
    if (this.commodityState() === false) return false;
    if (this.itemDescriptionState() === false) return false;
    return true;
  }

  async saveStockSku() {
    if (!this.validateForm()) return;

    const manufacturer = this.manufacturerForCompany;
    this.editStockSkuModel.manufacturerCode = manufacturer ? manufacturer.code : null;

    try {
      this.editStockSkuModel.saving = true;
      if (this.editStockSkuModel.isNew) {
        await stockSkuModuleState.createStockSku(this.editStockSkuModel.toCreateStockSkuRequest());
      } else {
        await stockSkuModuleState.updateStockSku(this.editStockSkuModel.toUpdateStockSkuRequest());
      }
      notifications.success("Stock SKU Saved", "Successfully saved stock SKU");
      await this.loadStockSkus();
      this.closeEditModal();
    } catch (ex) {
      notifications.error("Save Stock SKU", "Failed to save stock SKU", ex);
      this.editStockSkuModel.saving = false;
    }
  }

  closeEditModal() {
    this.editStockSkuModel.visible = false;
    this.editStockSkuModel = null;
  }

  async editStockSku(item: StockSkuResource = null, showModal: boolean = true) {
    this.editStockSkuModel = new EditStockSkuModel();
    this.editStockSkuModel.visible = showModal;

    if (item) {
      const stockSku = await stockSkuModuleState.getStockSku(item.id);
      this.editStockSkuModel.loading = false;
      this.editStockSkuModel.isNew = false;
      this.$set(this.editStockSkuModel, 'id', stockSku.id);
      this.$set(this.editStockSkuModel, 'companyId', stockSku.companyId);
      this.$set(this.editStockSkuModel, 'manufacturerCode', stockSku.manufacturerCode);
      this.$set(this.editStockSkuModel, 'sku', stockSku.sku);
      this.$set(this.editStockSkuModel, 'commodity', stockSku.commodity || stockSku.itemType);
      this.$set(this.editStockSkuModel, 'itemDescription', stockSku.itemDescription);
      this.$set(this.editStockSkuModel, 'isBulkItem', !!stockSku.isBulkItem);
      this.$set(this.editStockSkuModel, 'bulkScanCode', stockSku.bulkScanCode);
      this.$set(this.editStockSkuModel, 'isActive', !!stockSku.isActive);
    } else {
      this.editStockSkuModel.showForNewStockSku();
    }
  }

  get importManufacturerForCompany(): ManufacturerResource {
    if (!this.importCompanyId) return null;
    return this.importCompanyManufacturers.find(x => x.allowManualStockOrdersCreation) || null;
  }

  get importManufacturerDisplayName() {
    const manufacturer = this.importManufacturerForCompany;
    return manufacturer ? (manufacturer.agentManufacturerCode ?? manufacturer.manufacturerName) : "";
  }

  get canImport() {
    return !!(this.importCompanyId && this.importManufacturerForCompany && this.importFile);
  }

  showImportModal() {
    this.importModalVisible = true;
  }

  closeImportModal() {
    this.importModalVisible = false;
  }

  resetImportModal() {
    this.importCompanyId = null;
    this.importFile = null;
  }

  downloadTemplate() {
    const csv = "SKU,Commodity,ItemDescription,IsBulkItem,BulkScan,IsActive\n";

    const anchor = document.createElement('a');
    anchor.href = 'data:text/csv;charset=utf-8,' + encodeURIComponent(csv);
    anchor.target = '_blank';
    anchor.download = 'stock_sku_import_template.csv';
    anchor.click();
  }

  async uploadImportFile() {
    if (!this.canImport) return;

    this.importing = true;
    try {
      const manufacturer = this.importManufacturerForCompany;
      const response = await stockSkuModuleState.importStockSkus({
        file: this.importFile,
        companyId: this.importCompanyId,
        manufacturerCode: manufacturer.code
      });

      if (response.success) {
        notifications.success("Stock SKUs Imported", "Successfully imported stock SKUs");
        const importedCompanyId = this.importCompanyId;
        this.closeImportModal();
        if (this.companyIdFilter === importedCompanyId) {
          await this.loadStockSkus();
        }
      } else {
        notifications.error("Import Stock SKUs", "Failed to import stock SKUs");
      }
    } catch (ex) {
      notifications.error("Import Stock SKUs", "Failed to import stock SKUs", ex);
    }
    this.importing = false;
  }
}
